import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MISSION_ENTRY_SOURCE,
  cancelMissionEvolution,
  getMissionDetails,
} from "../../../Services/journey";
import { trackEvent } from "../../../Services/amplitude";
import { MISSION_EVENT, missionRoutes } from "./evolution";
import { forgetEvolutionChat } from "./evolutionChat";

/**
 * `GET /journey/mission` plus the evolution actions both the "Your Mission"
 * sheet and the Mission page offer. `enabled` defers the read until the sheet
 * opens. `entrySource` is what Evolve reports as `entry_source`.
 */
const useMission = ({ enabled = true, entrySource = MISSION_ENTRY_SOURCE.JOURNEY_MISSION } = {}) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(false);
  const [stopping, setStopping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await getMissionDetails();
      setDetails(payload || null);
      setError(false);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  // Explicit "stop evolving": discards any proposal; the Mission is unchanged.
  const onStop = async (evolutionSessionId) => {
    setStopping(true);
    try {
      await cancelMissionEvolution(evolutionSessionId);
      trackEvent(MISSION_EVENT.EVOLUTION_CANCELLED);
      forgetEvolutionChat(evolutionSessionId);
    } catch (e) {
      console.error("Failed to stop the mission evolution", e);
    } finally {
      setStopping(false);
      load();
    }
  };

  return {
    details,
    loading,
    error,
    reload: load,
    actions: {
      stopping,
      onStop,
      onEvolve: () => navigate(missionRoutes.evolve({ entry_source: entrySource })),
      onReview: (draftId) => navigate(missionRoutes.review(draftId)),
      onHistory: () => navigate(missionRoutes.history),
    },
  };
};

export default useMission;
